import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment-types'
import {createContext, useCallback, useContext, useMemo, useState} from 'react'

type CopilotAssignmentModalState = {
  isOpen: boolean
  repository: CopilotAssignmentRepository | null
  issueNumber: number | null
  onSuccess?: () => Promise<void>
}

type CopilotAssignmentModalContextValue = {
  modalState: CopilotAssignmentModalState
  openModal: (repository: CopilotAssignmentRepository, issueNumber: number, onSuccess: () => Promise<void>) => void
  closeModal: () => void
  handleAssignmentComplete: () => Promise<void>
}

const CopilotAssignmentModalContext = createContext<CopilotAssignmentModalContextValue | null>(null)

export const CopilotAssignmentModalProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [modalState, setModalState] = useState<CopilotAssignmentModalState>({
    isOpen: false,
    repository: null,
    issueNumber: null,
  })

  const openModal = useCallback(
    (repository: CopilotAssignmentRepository, issueNumber: number, onSuccess: () => Promise<void>) => {
      setModalState({
        isOpen: true,
        repository,
        issueNumber,
        onSuccess,
      })
    },
    [],
  )

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      repository: null,
      issueNumber: null,
    })
  }, [])

  const handleAssignmentComplete = useCallback(async () => {
    await modalState.onSuccess?.()
    closeModal()
  }, [closeModal, modalState])

  const contextValue: CopilotAssignmentModalContextValue = useMemo(
    () => ({
      handleAssignmentComplete,
      modalState,
      openModal,
      closeModal,
    }),
    [handleAssignmentComplete, modalState, openModal, closeModal],
  )

  return (
    <CopilotAssignmentModalContext.Provider value={contextValue}>{children}</CopilotAssignmentModalContext.Provider>
  )
}

export function useCopilotAssignmentModal(): CopilotAssignmentModalContextValue {
  const context = useContext(CopilotAssignmentModalContext)

  if (!context) {
    throw new Error('Must use `useCopilotAssignmentModal` in child of `<CopilotAssignmentModalProvider>`')
  }

  return context
}

try{ CopilotAssignmentModalContext.displayName ||= 'CopilotAssignmentModalContext' } catch {}
try{ CopilotAssignmentModalProvider.displayName ||= 'CopilotAssignmentModalProvider' } catch {}